import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Download, ShieldAlert, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CircleAvatar } from "@/components/CircleAvatar";
import { money } from "@/lib/blessed";
import { celebrate } from "@/lib/effects";
import { getExternalDonation, confirmExternal, readExternalPending, clearExternalPending, isPhoneish } from "@/lib/external";

const HOLD_MS = 3000;

// After paying the $2 fee, the donor lands here to see the QR/handle, sends the money
// in their own bank/Venmo app, then holds a button 3s to confirm (or cancels for a refund).
export default function ExternalSendPage() {
  const { id: pathId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = pathId || params.get("donation_id") || readExternalPending();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [progress, setProgress] = useState(0);
  const holdTimer = useRef(null);
  const rafId = useRef(null);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    getExternalDonation(id)
      .then(({ data }) => { setData(data); if (data.status === "sent_external") setConfirmed(true); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (confirmed) return;
    const before = (e) => { e.preventDefault(); e.returnValue = "You already paid the fee. Please confirm once you've sent the money."; return e.returnValue; };
    window.addEventListener("beforeunload", before);
    return () => window.removeEventListener("beforeunload", before);
  }, [confirmed]);

  const finishHold = async () => {
    try {
      await confirmExternal(id);
      clearExternalPending();
      setConfirmed(true);
      celebrate();
      setTimeout(() => navigate("/live"), 1400);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm — did the fee go through?"); setProgress(0); }
  };

  const startHold = () => {
    const start = Date.now();
    const tick = () => {
      const t = (Date.now() - start) / HOLD_MS;
      setProgress(Math.min(1, t));
      if (t < 1) rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);
    holdTimer.current = setTimeout(finishHold, HOLD_MS);
  };
  const cancelHold = () => { clearTimeout(holdTimer.current); cancelAnimationFrame(rafId.current); if (!confirmed) setProgress(0); };

  const download = () => {
    if (!data?.qr_url) return;
    const a = document.createElement("a");
    a.href = data.qr_url; a.download = `blessed-${data.method}-qr.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-brand-primary" size={34} /></div>;
  if (!id || !data) return <div className="p-8 text-center text-ink-tertiary" data-testid="external-notfound">We couldn't find this send. It may have expired.</div>;

  const methodLabel = data.method === "zelle" ? "Zelle" : "Venmo";
  const isPhone = data.method === "zelle" || isPhoneish(data.handle);
  const shownHandle = isPhone ? data.handle : `@${(data.handle || "").replace(/^@/, "")}`;

  if (confirmed) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 grid place-items-center text-center" data-testid="external-confirmed">
        <div className="w-20 h-20 rounded-pill grid place-items-center text-white mb-6 animate-pop-in" style={{ background: "#22C55E" }}><Check size={40} /></div>
        <h1 className="text-3xl font-black">Sent via {methodLabel}! 💜</h1>
        <p className="text-base text-ink-tertiary font-semibold mt-3">Thank you for blessing {data.recipient_name}. Taking you to the live feed…</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-5 pb-28" data-testid="external-send-page">
      <div className="flex items-center gap-2 rounded-pill px-4 py-2.5 mb-5 text-sm font-black" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="external-warning">
        <ShieldAlert size={18} /> Don't close this page until you confirm you've sent it.
      </div>

      <div className="flex items-center gap-3 mb-4">
        <CircleAvatar src={data.recipient_picture} name={data.recipient_name} size={52} ring="linear-gradient(135deg,#FFD93D,#FF8A3D)" />
        <div>
          <h1 className="text-xl font-black leading-tight">Send {money(data.amount)} to {data.recipient_name}</h1>
          <p className="text-sm font-bold text-ink-tertiary">via {methodLabel}</p>
        </div>
      </div>

      {data.qr_url && (
        <div className="bg-white rounded-lg p-4 grid place-items-center border border-border mb-3">
          <img src={data.qr_url} alt={`${methodLabel} QR`} className="w-56 h-56 object-contain" data-testid="external-qr" />
        </div>
      )}

      {data.handle && (
        <div className="text-center mb-4">
          <div className="text-xs font-black uppercase tracking-wide text-ink-tertiary mb-1">
            {data.qr_url ? "or send to " : "send to "}{isPhone ? "phone" : "@user"}
          </div>
          <div data-testid="external-handle" className="text-2xl font-black select-all">{shownHandle}</div>
        </div>
      )}

      {data.qr_url && (
        <Button data-testid="external-download" variant="outline" onClick={download} className="w-full h-11 rounded-pill font-black border-border mb-5">
          <Download size={16} className="mr-1" /> Download QR
        </Button>
      )}

      <button data-testid="external-hold-confirm"
        onMouseDown={startHold} onMouseUp={cancelHold} onMouseLeave={cancelHold}
        onTouchStart={startHold} onTouchEnd={cancelHold}
        className="relative w-full h-14 rounded-pill font-black text-white overflow-hidden select-none" style={{ background: "#16A34A" }}>
        <span className="absolute inset-y-0 left-0 bg-black/25" style={{ width: `${progress * 100}%`, transition: "width 60ms linear" }} />
        <span className="relative z-10">{progress > 0 ? "Keep holding…" : `Hold to confirm I sent ${money(data.amount)}`}</span>
      </button>
      <p className="text-center text-xs text-ink-tertiary mt-2">Press and hold for 3 seconds once you've sent the money.</p>
    </div>
  );
}
